import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { ROUTES } from './routes';

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Wait for auth check to complete before redirecting — prevents
  // refresh on any protected route (e.g. /claude-agents) from bouncing
  // to /login → /start due to a momentary unauthenticated state.
  if (isLoading) return null;

  if (!isAuthenticated) {
    // Save the full intended path (e.g. /artifacts/:guid) so LoginPage
    // can redirect there after a successful sign-in.
    const intended = location.pathname + location.search;
    if (intended && intended !== '/' && intended !== ROUTES.LOGIN) {
      sessionStorage.setItem('redirectAfterLogin', intended);
    }
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <>{children}</>;
};

export const GuestRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  // loginPending is set by LoginPage for the duration of login() + getDefaultPage().
  // While it's set, keep the form visible so the button spinner is never hidden by a blank screen.
  const loginInProgress = !!localStorage.getItem('loginPending');

  // Normal page-load auth check — blank screen prevents form flash on refresh
  if (isLoading && !loginInProgress) return null;

  // Don't redirect while LoginPage is handling navigation itself
  if (isAuthenticated && !loginInProgress) {
    return <Navigate to={ROUTES.START} replace />;
  }

  return <>{children}</>;
};
